import React from 'react';
import { Col, Form, FormGroup, FormControl, ControlLabel, HelpBlock, Button } from 'react-bootstrap';
import DatePicker from 'react-bootstrap-date-picker';
import Scroll from 'react-scroll';
import rest from 'rest';

import { REST_API_URL, APP_PATH, PERMISSION_READ } from "../../Utils/Global";
import { getDateFromString, getIsoStringFromDate, getStringFromDate } from "../../Utils/Date";
import { cloneObject } from "../../Utils/Utils";

import Status from '../Helper/Status';
import AdvancedCombobox from '../Helper/AdvancedCombobox';
import ControlledGrid from '../Helper/ControlledGrid';
import GeometryGrid from '../Helper/GeometryGrid';
import FileUpload from '../Helper/FileUpload';
import PermissionCheck from '../Permissions/PermissionCheck';
import AbstractEntity from '../Entity/AbstractEntity';
import TypeDocument from '../Entity/TypeDocument';
import Auteur from '../Entity/Auteur';
import Thematique from '../Entity/Thematique';
import MotCle from '../Entity/MotCle';

/**
* Class Document
* Document view component
**/
class Document extends AbstractEntity {
  /**
  * props
  * mode : readonly/add/edit
  * path : rest api path for the object's type
  * data.object : instance data
  **/

  /**
  * getClassName
  * Returns the class name as String (once bundled the class name changes)
  **/
  static getClassName() {
    return "Document";
  }

  /**
  * constructor
  * Constructor
  **/
  constructor(props) {
		super(props);

    this.handleDatePublicationDocumentChange = this.handleDatePublicationDocumentChange.bind(this);
    this.handleTypeDocumentChange = this.handleTypeDocumentChange.bind(this);
  }

  /**
  * componentWillMount
  * Pre-rendering component function : loading object if it doesn't exist
  **/
  componentWillMount() {

    if(this.state.object === undefined) {
      const pathName = this.getObjectPath();

      rest({method: 'GET', path: REST_API_URL + pathName}).then(response => {
        let state = this.state;
        let obj = JSON.parse(response.entity);

        obj.datePublicationDocument = getDateFromString(obj.datePublicationDocument);

        state.object = obj;

        this.setState(state)
      });
    }
  }

  /**
  * handleDatePublicationDocumentChange
  * Modify the state datePublicationDocument property from datePublicationDocument field change
  **/
  handleDatePublicationDocumentChange(dateString, formattedDate) {
    let objState = this.state;
    objState.object["datePublicationDocument"] = (dateString !== null) ? new Date(dateString) : null;
    this.setState(objState);
  }

  /**
  * handleTypeDocumentChange
  * Handle TypeDocument onChange event
  **/
  handleTypeDocumentChange(typeDocument) {
    let objState = this.state;
    objState.object["idTypeDocument"] = (typeDocument) ? typeDocument.idTypedoc : null;
    this.setState(objState);
  }

  /**
  * render
  * Rendering component function : display the view
  **/
  render() {

    if(this.state.object === undefined) {
        return <div></div>;
    }

    // Form status information
    const status = <Status parent={this} type={this.state.status.type} message={this.state.status.message} />;

    // Transforms date fields
    let datePublicationDocument = null;

    if(this.state.object !== undefined) {
      if(this.state.object.datePublicationDocument) {

        datePublicationDocument = this.state.object.datePublicationDocument;

        if(typeof datePublicationDocument === "string") {
          datePublicationDocument = getDateFromString(datePublicationDocument);
        }

        datePublicationDocument = getIsoStringFromDate(datePublicationDocument);
      }
    }

    const backToDocument = <Button bsStyle="link" onClick={this.goBack} className="btnBack"><span className="glyphicon glyphicon-arrow-left" /> Retour</Button>;

    return (
      <div>
        {/* Status information */}
        {status}
        {backToDocument}
        <Form horizontal onSubmit={this.handleSubmit}>
          <HelpBlock>Formulaire du document.</HelpBlock>

          {/* Form content */}
          <FormGroup controlId="idDocument" className="customHidden">
            <Col componentClass={ControlLabel} sm={2}>Identifiant</Col>
            <Col sm={1}><FormControl type="text" value={this.getObjectProperty("idDocument")} disabled="true" /></Col>
          </FormGroup>

          <FormGroup controlId="libelleDocument">
            <Col componentClass={ControlLabel} sm={2}><ControlLabel>Libellé</ControlLabel></Col>
            <Col componentClass={ControlLabel} sm={6}><FormControl type="text" onChange={this.handleChange} value={this.getObjectProperty("libelleDocument")} disabled={this.getReadOnly()} /></Col>
          </FormGroup>

          <FormGroup controlId="datePublicationDocument">
            <Col componentClass={ControlLabel} sm={2}>Date de publication</Col>
            <Col sm={2}><DatePicker dateFormat="DD/MM/YYYY" value={datePublicationDocument} onChange={this.handleDatePublicationDocumentChange} disabled={this.getReadOnly()} /></Col>
          </FormGroup>

          <FormGroup controlId="file">
            <Col componentClass={ControlLabel} sm={2}>Fichier</Col>
            <Col sm={6}>
              <FileUpload ref="file" existing={(this.getObjectProperty("cheminStockage")) ? this.getObjectProperty("cheminStockage") : undefined}
                getPath={this.getObjectPath() + "/file"} mode={this.state.mode} />
            </Col>
          </FormGroup>

          {/* TypeDocument ManyToOne */}
          <PermissionCheck component={TypeDocument.getClassName()} permission={PERMISSION_READ}>
            <FormGroup controlId="idTypeDocument">
              <Col componentClass={ControlLabel} sm={2}>Type de document</Col>
              <Col sm={2}><AdvancedCombobox path="typedocuments" objectPath={this.getObjectPath() + "typedocuments"} objectClass={TypeDocument} url={REST_API_URL + "typedocuments/"}
                optionValueKey="idTypedoc" optionLabelKey="libelle" default={this.getObjectProperty("idTypeDocument")} onChange={this.handleTypeDocumentChange} disabled={this.getReadOnly()}
                enableSearch={true} /></Col>
            </FormGroup>
          </PermissionCheck>

          {/* Auteur ManyToMany */}
          <PermissionCheck component={Auteur.getClassName()} permission={PERMISSION_READ}>
            <FormGroup controlId="auteurs">
              <Col componentClass={ControlLabel} sm={2}>Auteurs</Col>
              <Col sm={6}>
                <ControlledGrid ref="auteursGrid" path="auteurs" objectPath={"documents_auteurs/" + this.getObjectProperty("idDocument") + "/auteurs"}
                objectClass={Auteur} mode={this.state.mode} cbxUrl={REST_API_URL + "documents_auteurs/" + this.getObjectProperty("idDocument") + "/auteurs/inverse/"}
                optionValueKey="id" optionLabelKey="libelle" />
              </Col>
            </FormGroup>
          </PermissionCheck>

          {/* Thematique ManyToMany */}
          <PermissionCheck component={Thematique.getClassName()} permission={PERMISSION_READ}>
            <FormGroup controlId="thematiques">
              <Col componentClass={ControlLabel} sm={2}>Thématiques</Col>
              <Col sm={6}>
                <ControlledGrid ref="thematiquesGrid" path="thematiques" objectPath={"documents_thematiques/" + this.getObjectProperty("idDocument") + "/thematiques"}
                objectClass={Thematique} mode={this.state.mode} cbxUrl={REST_API_URL + "documents_thematiques/" + this.getObjectProperty("idDocument") + "/thematiques/inverse/"}
                optionValueKey="idThematique" optionLabelKey="libelle" />
              </Col>
            </FormGroup>
          </PermissionCheck>

          {/* MotCle ManyToMany */}
          <PermissionCheck component={MotCle.getClassName()} permission={PERMISSION_READ}>
            <FormGroup controlId="motCles">
              <Col componentClass={ControlLabel} sm={2}>Mots clé</Col>
              <Col sm={6}>
                <ControlledGrid ref="motclesGrid" path="motcles" objectPath={"documents_motcles/" + this.getObjectProperty("idDocument") + "/motcles"} objectClass={MotCle}
                mode={this.state.mode} cbxUrl={REST_API_URL + "documents_motcles/" + this.getObjectProperty("idDocument") + "/motcles/inverse/"}
                optionValueKey="idMotCle" optionLabelKey="libelle" />
              </Col>
            </FormGroup>
          </PermissionCheck>

          {/* Geom OneToMany */}
          {(this.state.mode !== "add") ?
            <GeometryGrid title="Géométries" path="documents_geoms" objectPath={"documents_geoms/" + this.getObjectProperty("idDocument")} objectClass={Document} mode={this.state.mode}
              queryLayers={"DocumentCreate"} allowDraw={false} selectionMultiple={true} typeGeometry="all" withImportCsv={false} withImportPoint={false} /> : null
          }

          {/* Submit button */}
          {this.state.mode !== "readonly" ?
            <FormGroup>
              <Col smOffset={7} sm={1}>
                <Button bsStyle="success" type="submit">Sauvegarder</Button>
              </Col>
            </FormGroup> : ""
          }

  			</Form>

      </div>
    );
  }

  /**
  * getIdField
  * Returns the object idField
  **/
  getIdField() {
    return "idDocument";
  }

  /**
  * sendFormData
  * Saves the data
  **/
  sendFormData () {
    let method;
    const path = REST_API_URL + this.getObjectPath();
    let documentObj = cloneObject(this.state.object);

    if(this.state.mode === "edit") {
      method = "PUT";
    } else if (this.state.mode === "add") {
      method = "POST";
      if(this.refs.auteursGrid !== undefined) documentObj.auteurs = this.refs.auteursGrid.getArrayToSave();
      if(this.refs.thematiquesGrid !== undefined) documentObj.thematiques = this.refs.thematiquesGrid.getArrayToSave();
      if(this.refs.motclesGrid !== undefined) documentObj.motCles = this.refs.motclesGrid.getArrayToSave();
    }

    if(typeof documentObj.datePublicationDocument !== "string") {
      documentObj.datePublicationDocument = getStringFromDate(documentObj.datePublicationDocument);
    }

    Scroll.animateScroll.scrollToTop({
      duration: 1000,
      delay: 100,
      smooth: true
    });

    rest({
      method: method,
      path: path,
      entity: JSON.stringify(documentObj),
      headers: {
  			'Content-Type': 'application/json'
  		}
    }).then(response => {
      if(response.status.code === 200) {
        this.saveGrids();
        this.uploadFile({
          status: {
            type: 'success',
            message: 'Document sauvegardé.'
          },
          object: JSON.parse(response.entity)
        });
      } else if(response.status.code === 201) {
        const newObj = JSON.parse(response.entity);
        this.uploadFile({
          status: {
            type: 'success',
            message: 'Document créé.'
          },
          mode: 'edit',
          object: newObj
        });

      } else {
        this.setState({
          status: {
            type: 'danger',
            message: 'Erreur de sauvegarde.'
          }
        });
      }
  	}, response => {
      this.setState({
        status: {
          type: 'danger',
          message: 'Problème d\'appel au serveur.'
        }
      });
		});
  }

  uploadFile(newState) {
    const file = this.refs.file.getFile();
    const documentId = newState.object[this.getIdField()];

    if(file !== undefined) {
      let data = new FormData();
      data.append('file', file);
      data.append('name', file.name);
      data.append('description', file.name);

      rest({
        method: 'POST',
        path: REST_API_URL + '/documents/' + documentId + '/file',
        entity: data,
        headers: {
    			'Content-Type': 'multipart/form-data'
    		}
      }).then(response => {
        let state = newState;
        let obj=state.object;

        if(response.status.code === 200) {
          state = {
            status: {
              type: 'success',
              message: 'Document sauvegardé.'
            },
            mode: 'edit',
            object: obj
          };
        } else {
          state = {
            status: {
              type: 'danger',
              message: 'Document sauvegardé, mais l\'upload du fichier a échoué.'
            },
            mode: 'edit'
          };
        }

        if(this.state.mode === "add") {
          //this.goBack();
          console.log(response.entity);
          state.object.cheminStockage=JSON.parse(response.entity).cheminStockage;
          this.replaceHistory(APP_PATH + "/documents/" + documentId + "/edit", state);
        } else {
          this.setState(state);
        }

      });

    } else {

      if(this.refs.file.getExisting() === false) {
        rest({
          method: 'DELETE',
          path: REST_API_URL + '/documents/' + documentId + '/file'
        }).then(response => {
          let state = newState;
          if(response.status.code === 202) {
            state = {
              status: {
                type: 'success',
                message: 'Document sauvegardé.'
              },
              mode: 'edit',
              object: JSON.parse(response.entity)
            };
          } else {
            state = {
              status: {
                type: 'danger',
                message: 'Document sauvegardé, mais l\'upload du fichier a échoué.'
              },
              mode: 'edit'
            };
          }

          if(this.state.mode === "add") {
            this.replaceHistory(APP_PATH + "/documents/" + documentId + "/edit", state);
          } else {
            this.setState(state);
          }
        });
      } else {
        if(this.state.mode === "add") {
          this.replaceHistory(APP_PATH + "/documents/" + documentId + "/edit", newState);
        } else {
          this.setState(newState);
        }
      }

    }

  }

  /**
  * saveGrids
  * Saves the ManyToMany components
  **/
  saveGrids() {
    if(this.refs.auteursGrid !== undefined) this.refs.auteursGrid.saveAll();
    if(this.refs.thematiquesGrid !== undefined) this.refs.thematiquesGrid.saveAll();
    if(this.refs.motclesGrid !== undefined) this.refs.motclesGrid.saveAll();
  }

}

export default Document;



// WEBPACK FOOTER //
// ./src/views/Entity/Document.js